import React, { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { epointsApi } from "../services/epointsApi";
import PaymentForm from "../components/stripe/PaymentForm";
import EpointBalance from "../components/epoints/EpointBalance";
import {
  CurrencyEuroIcon,
  PlusIcon,
  MinusIcon,
  InformationCircleIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import { useCurrency } from "../contexts/CurrencyContext";
import { formatSecondaryNumber } from "../utils/currency";

const PRESET_AMOUNTS = [10, 25, 50, 100, 250];
const MIN_AMOUNT = 0;

interface VatCalculation {
  netAmount: number;
  vatPercent: number;
  vatAmount: number;
  grossAmount: number;
  specialText: string | null;
  scenario: string;
  explanation: string;
}

interface VatCalculationResponse {
  success: boolean;
  data: VatCalculation;
  message: string;
}

const Deposit: React.FC = () => {
  const { t } = useTranslation();
  const { rate } = useCurrency();
  const navigate = useNavigate();
  const [selectedAmount, setSelectedAmount] = useState<number>(25);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [vatCalculation, setVatCalculation] = useState<VatCalculation | null>(
    null,
  );
  const [isCalculatingVat, setIsCalculatingVat] = useState(false);
  const lastVatCalculationRef = React.useRef<number>(0);
  const lastCalculatedAmountRef = React.useRef<number | null>(null);
  const isCalculatingRef = React.useRef<boolean>(false);
  const MIN_VAT_CALCULATION_INTERVAL = 2000; // Minimum 2 seconds between VAT calculations

  const { data: balance } = useQuery({
    queryKey: ["epoint-balance"],
    queryFn: epointsApi.getBalance,
  });

  const calculateVat = useCallback(async () => {
    if (selectedAmount <= 0) {
      setVatCalculation(null);
      lastCalculatedAmountRef.current = null;
      return;
    }

    // Prevent multiple simultaneous calculations
    if (isCalculatingRef.current) {
      return;
    }

    // Skip if same amount was just calculated
    if (lastCalculatedAmountRef.current === selectedAmount) {
      const now = Date.now();
      const timeSinceLastCalculation = now - lastVatCalculationRef.current;
      if (timeSinceLastCalculation < MIN_VAT_CALCULATION_INTERVAL) {
        return;
      }
    }

    // Throttle: Don't calculate if last calculation was too recent
    const now = Date.now();
    const timeSinceLastCalculation = now - lastVatCalculationRef.current;
    if (timeSinceLastCalculation < MIN_VAT_CALCULATION_INTERVAL) {
      return;
    }

    isCalculatingRef.current = true;
    setIsCalculatingVat(true);
    lastVatCalculationRef.current = now;
    lastCalculatedAmountRef.current = selectedAmount;
    try {
      const response = await apiService.post<VatCalculationResponse>(
        "/dealer-receipts/calculate-vat-preview",
        {
          netAmount: selectedAmount,
        },
      );

      // Backend returns { success, data, message } where data contains VatCalculation
      const vatData = (response.data as any).data || response.data;
      setVatCalculation(vatData);
    } catch (error: any) {
      console.error("[Deposit] VAT calculation failed:");

      // Fallback: Assume Germany scenario (19% VAT)
      const fallbackVatPercent = 19;
      const fallbackVatAmount =
        Math.round(selectedAmount * (fallbackVatPercent / 100) * 100) / 100;
      const fallbackGrossAmount = selectedAmount + fallbackVatAmount;

      setVatCalculation({
        netAmount: selectedAmount,
        vatPercent: fallbackVatPercent,
        vatAmount: fallbackVatAmount,
        grossAmount: fallbackGrossAmount,
        specialText: null,
        scenario: "germany",
        explanation: "Fallback calculation (VAT service unavailable)",
      });

      toast.error("Could not calculate VAT from server, using default rate");
    } finally {
      isCalculatingRef.current = false;
      setIsCalculatingVat(false);
    }
  }, [selectedAmount]);

  // Calculate VAT whenever amount changes
  useEffect(() => {
    // Only calculate if amount actually changed
    if (lastCalculatedAmountRef.current !== selectedAmount) {
      calculateVat();
    }
  }, [selectedAmount, calculateVat]);

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
    setShowPaymentForm(false);
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= MIN_AMOUNT) {
      setSelectedAmount(numValue);
    }
    setShowPaymentForm(false);
  };

  const handleProceedToPayment = () => {
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = (depositId: string) => {
    toast.success(t("deposit.depositSuccess"));
    navigate("/history");
    // navigate(`/deposit-success?depositId=${depositId}`);
  };

  const handlePaymentError = (error: string) => {
    setShowPaymentForm(false);
    navigate("/payment-failed", { state: { reason: error } });
  };

  const adjustAmount = (delta: number) => {
    const newAmount = Math.max(MIN_AMOUNT, selectedAmount + delta);
    setSelectedAmount(newAmount);
    setCustomAmount(newAmount.toString());
    setShowPaymentForm(false);
  };

  const epointsToReceive = selectedAmount * 100;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      {/* <div className="text-center">
        <h1 className="text-3xl font-bold text-secondary">
          {t('deposit.title')}
        </h1>
        <p className="mt-2 text-gray-600">
          {t('deposit.subtitle')}
        </p>
      </div> */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Amount Selection */}
        <div className="space-y-6">
          {/* Current Balance */}
          {/* <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-secondary mb-4">
              {t('deposit.currentBalance')}
            </h2>
            <EpointBalance balance={balance} />
          </div> */}

          {/* Amount Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-secondary mb-4">
              {t("deposit.selectAmount")}
            </h2>

            {/* Preset Amounts */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {PRESET_AMOUNTS.map((amount) => {
                // Calculate expected gross amount for this preset
                const isSelected = selectedAmount === amount && !customAmount;
                const displayGross =
                  isSelected && vatCalculation
                    ? vatCalculation.grossAmount
                    : amount * 1.19; // Approximate for display

                return (
                  <button
                    key={amount}
                    onClick={() => handleAmountSelect(amount)}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      isSelected
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : "border-gray-300 hover:border-primary-300 text-secondary"
                    }`}
                  >
                    <div className="text-lg font-semibold">€{amount}</div>
                    <div className="text-xs text-gray-500">
                      {(amount * 100).toLocaleString()} ePoints
                    </div>
                    {rate && (
                      <div className="text-xs text-gray-400">
                        ≈ {formatSecondaryNumber(amount, rate)}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Custom Amount */}
            <div className="space-y-4">
              {/* <label className="block text-sm font-medium text-gray-700">
                {t('deposit.customAmount')}
              </label> */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => adjustAmount(-1)}
                  disabled={selectedAmount <= MIN_AMOUNT}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MinusIcon className="h-4 w-4" />
                </button>

                <div className="relative flex-1">
                  <CurrencyEuroIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    min={MIN_AMOUNT}
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => handleCustomAmountChange(e.target.value)}
                    placeholder={t("deposit.enterAmount")}
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-secondary placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <button
                  onClick={() => adjustAmount(1)}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* VAT Calculation Summary */}
          {/* {vatCalculation && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Payment Overview</h4>

              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Net Amount</span>
                <span className="text-secondary">
                  €{vatCalculation.netAmount.toFixed(2)}
                </span>
              </div>

              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">
                  VAT ({vatCalculation.vatPercent}%)
                </span>
                <span className="text-secondary">
                  €{vatCalculation.vatAmount.toFixed(2)}
                </span>
              </div>

              <div className="border-t border-gray-300 my-2"></div>

              <div className="flex items-center justify-between mb-3">
                <span className="text-base font-semibold text-gray-700">Total Payment Amount</span>
                <span className="text-lg font-bold text-primary-600">
                  €{vatCalculation.grossAmount.toFixed(2)}
                </span>
              </div>
            </div>
          )} */}

          {/* Exchange Rate Info */}
          {/* <div className="bg-primary-50/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <InformationCircleIcon className="h-5 w-5 text-primary-500 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-primary-900">
                  {t('deposit.exchangeRateTitle')}
                </h3>
                <p className="text-sm text-primary-700 mt-1">
                  {t('deposit.exchangeRateValue')}
                </p>
                <p className="text-xs text-primary-600 mt-1">
                  {t('deposit.youWillReceive', { points: epointsToReceive.toLocaleString(), amount: selectedAmount.toFixed(2) })}
                </p>
              </div>
            </div>
          </div> */}

          {/* Proceed Button */}
          {!showPaymentForm && (
            <button
              onClick={handleProceedToPayment}
              disabled={isCalculatingVat || !vatCalculation}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCalculatingVat ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                </>
              ) : (
                <>
                  <CreditCardIcon className="h-4 w-4 mr-2" />
                  {t("deposit.proceedToPayment")}
                </>
              )}
            </button>
          )}
        </div>

        {/* Right Column - Payment Form */}
        <div className="lg:sticky lg:top-6">
          {showPaymentForm ? (
            <div className="space-y-4">
              <button
                onClick={() => setShowPaymentForm(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                {t("deposit.backToAmountSelection")}
              </button>
              <PaymentForm
                amount={selectedAmount}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </div>
          ) : (
            <div className="bg-gray-100 rounded-lg p-8 text-center">
              <CreditCardIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary mb-2">
                {t("deposit.paymentForm")}
              </h3>
              <p className="text-gray-500">
                {t("deposit.selectAmountToProceed")}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Security Features */}
      {/* <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-secondary mb-4">
          Secure & Trusted
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
            <span className="text-gray-600">
              256-bit SSL Encryption
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
            <span className="text-gray-600">
              PCI DSS Compliant
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
            <span className="text-gray-600">
              Instant Processing
            </span>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default Deposit;
